import { eyeOutline, mailOpenOutline, mailOutline, warningOutline } from 'ionicons/icons';
import type { BrevoInvoiceEmailStatus } from '../services/zohoInvoicingService';

export interface EmailStateIcon {
  icon: string;
  label: string;
  color: string;
}

// What Zoho itself knows about an email (it can't see past the hand-off to the Brevo relay).
export interface ZohoEmailFlags {
  viewed?: boolean;
  opened?: boolean;
  sent?: boolean;
}

// One ladder for every email status icon, highest first:
// failed (red) → viewed (eye) → opened → opened by mail app → delivered → sent.
// Returns null when nothing is known (not sent yet).
export function brevoEmailState(
  brevo: BrevoInvoiceEmailStatus | undefined,
  zoho: ZohoEmailFlags,
  tr: (key: string) => string
): EmailStateIcon | null {
  const delivered = brevo?.deliveredTime ?? 0;
  const opened = brevo?.firstOpenedTime ?? 0;
  const clicked = brevo?.clickedTime ?? 0;
  const proxy = brevo?.proxyTime ?? 0;
  const failed = brevo?.failedTime ?? 0;
  const viewed = !!zoho.viewed || clicked > 0;
  const latestGood = Math.max(delivered, opened, clicked, proxy);

  // Failed: the latest email bounced or was blocked and nothing has succeeded since
  if (!viewed && failed > latestGood) {
    const reason = brevo?.failedReason ? ` — ${brevo.failedReason}` : '';
    return { icon: warningOutline, label: `${tr('pages.InvoiceList.failed')}${reason}`, color: 'var(--ion-color-danger)' };
  }
  if (viewed) return { icon: eyeOutline, label: tr('pages.InvoiceList.viewed'), color: '#f0a500' };
  if (zoho.opened || opened > 0) return { icon: mailOpenOutline, label: tr('pages.InvoiceList.opened'), color: 'var(--ion-color-success)' };
  // Only a mail-app proxy loaded it (Apple Mail privacy / Gmail image proxy) — may not be a person
  if (proxy > 0) return { icon: mailOpenOutline, label: tr('pages.InvoiceList.openedByMailApp'), color: '#a8d8b5' };
  if (delivered > 0) return { icon: mailOutline, label: tr('pages.InvoiceList.delivered'), color: '#7ac98f' };
  if (zoho.sent) return { icon: mailOutline, label: tr('pages.InvoiceList.sent'), color: 'var(--ion-color-primary)' };
  return null;
}
